import { Head, router } from "@inertiajs/react"
import { Download, FileSpreadsheet } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency, formatDate } from "@/lib/utils"
import { profitLossPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface CategoryRow {
  name: string
  type: "expense" | "revenue"
  color: string
  total: number
}

interface ProfitLossProps {
  revenue: number
  expenses: number
  net_profit: number
  taxes: number
  net_income: number
  by_category: CategoryRow[]
  start_date: string
  end_date: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "P&L Report", href: profitLossPath() },
]

const PRESETS = [
  { label: "This Month", start: firstOfMonth(0), end: endOfMonth(0) },
  { label: "Last Month", start: firstOfMonth(-1), end: endOfMonth(-1) },
  { label: "This Quarter", start: firstOfQuarter(), end: today() },
  { label: "YTD", start: firstOfYear(), end: today() },
  {
    label: "Previous Year",
    start: `${new Date().getFullYear() - 1}-01-01`,
    end: `${new Date().getFullYear() - 1}-12-31`,
  },
]

function today() {
  return new Date().toISOString().split("T")[0]
}

function firstOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset, 1)
  return d.toISOString().split("T")[0]
}

function endOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset + 1, 0)
  return d.toISOString().split("T")[0]
}

function firstOfQuarter() {
  const d = new Date()
  const q = Math.floor(d.getMonth() / 3) * 3
  return new Date(d.getFullYear(), q, 1).toISOString().split("T")[0]
}

function firstOfYear() {
  return `${new Date().getFullYear()}-01-01`
}

export default function ProfitLoss({
  revenue,
  expenses,
  net_profit,
  taxes,
  net_income,
  by_category,
  start_date,
  end_date,
}: ProfitLossProps) {
  const [customStart, setCustomStart] = useState(start_date)
  const [customEnd, setCustomEnd] = useState(end_date)
  const [showCustom, setShowCustom] = useState(false)

  const revenueCategories = by_category.filter((c) => c.type === "revenue")
  const expenseCategories = by_category.filter((c) => c.type === "expense")

  function applyPreset(start: string, end: string) {
    setShowCustom(false)
    router.get(
      profitLossPath(),
      { start_date: start, end_date: end },
      { preserveState: true },
    )
  }

  function applyCustom() {
    router.get(
      profitLossPath(),
      { start_date: customStart, end_date: customEnd },
      { preserveState: true },
    )
  }

  function exportCsv() {
    window.location.href = `${profitLossPath()}.csv?start_date=${start_date}&end_date=${end_date}`
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="P&L Report" />

      <div className="mx-auto w-full max-w-3xl space-y-6 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Profit & Loss</h2>
            <p className="text-muted-foreground text-sm">
              {formatDate(start_date)} to {formatDate(end_date)}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant={
                start_date === preset.start && end_date === preset.end
                  ? "default"
                  : "outline"
              }
              size="sm"
              onClick={() => applyPreset(preset.start, preset.end)}
            >
              {preset.label}
            </Button>
          ))}
          <Button
            variant={showCustom ? "default" : "outline"}
            size="sm"
            onClick={() => setShowCustom(!showCustom)}
          >
            Custom
          </Button>
        </div>

        {showCustom && (
          <div className="flex items-end gap-3">
            <div className="grid gap-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={applyCustom}>
              Apply
            </Button>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(revenue)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(expenses)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Operating Profit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${net_profit >= 0 ? "text-green-600" : "text-red-600"}`}
              >
                {formatCurrency(net_profit)}
              </div>
            </CardContent>
          </Card>
        </div>

        {(taxes > 0 || net_income !== net_profit) && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Taxes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">
                  {formatCurrency(taxes)}
                </div>
              </CardContent>
            </Card>
            <Card className="md:col-start-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Net Income
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${net_income >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {formatCurrency(net_income)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {by_category.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <FileSpreadsheet className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-sm">
                No entries for this period. Try a different date range.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {revenueCategories.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {revenueCategories.map((cat) => (
                      <div
                        key={cat.name}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="text-sm">{cat.name}</span>
                          {cat.name === "Uncategorized" && (
                            <Badge
                              variant="outline"
                              className="text-xs text-amber-600"
                            >
                              Uncategorized
                            </Badge>
                          )}
                        </div>
                        <span className="font-mono text-sm font-medium text-green-600">
                          {formatCurrency(cat.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {expenseCategories.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">
                    Expenses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {expenseCategories.map((cat) => (
                      <div
                        key={cat.name}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="text-sm">{cat.name}</span>
                          {cat.name === "Uncategorized" && (
                            <Badge
                              variant="outline"
                              className="text-xs text-amber-600"
                            >
                              Uncategorized
                            </Badge>
                          )}
                        </div>
                        <span className="font-mono text-sm font-medium text-red-600">
                          {formatCurrency(cat.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
